import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    Home as HomeIcon,
    Group as GroupIcon,
    CheckCircle as CheckCircleIcon,
    WarningAmber as WarningAmberIcon,
    EmojiPeople as EmojiPeopleIcon,
    TipsAndUpdates as TipsAndUpdatesIcon,
    MenuBook as MenuBookIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    1.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('introduction')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    魅力的な導入
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    2.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('reasons-merits')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    きっかけとルームシェアのメリット
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    3.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('challenges')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    直面した課題と解決策
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    4.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('relation-changes')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    人間関係の変化と成長
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    5.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('advice')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    ルームシェア経験者からのアドバイス
                                </Link>
                            </ListItem>
                            <ListItem sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ minWidth: 32, minHeight: 32, bgcolor: '#f5f5f5', color: '#888', fontWeight: 'bold', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, fontSize: '1.1rem' }}>
                                    6.
                                </Box>
                                <Link component="button" onClick={() => scrollToSection('summary')} color="inherit" underline="hover" sx={{ cursor: 'pointer', fontSize: '0.95rem' }}>
                                    まとめ
                                </Link>
                            </ListItem>
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}
            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア体験のリアルな世界へようこそ
                </Typography>
                <Box sx={{ my: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        近年、<strong>ルームシェア</strong>は学生だけでなく、社会人やフリーランスなど幅広い世代・職業の間で人気を集めています。賃貸物件のコストを抑えられるだけでなく、多様な価値観と出会えるこの住まい方。ですが、実際にどんな日常が待っているのでしょうか？
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        本記事では、<Chip label="学生" color="primary" size="small" sx={{ mr: 1 }} /> <Chip label="社会人" color="primary" size="small" sx={{ mr: 1 }} /> <Chip label="フリーランス" color="primary" size="small" /> それぞれの立場から、実際の体験談を交えながら「きっかけ」「メリット」「直面した課題」「人間関係の変化」「成功するルームシェアのコツ」まで、専門家の視点で深掘りしていきます。
                    </Typography>
                </Box>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
                    
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    ※写真はイメージですが、実際のルームシェア生活における雰囲気を伝えています
                </Typography>
            </Box>

            {/* きっかけとメリット */}
            <Box id="reasons-merits" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    きっかけとルームシェアのメリット
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    {/* 学生の体験談 */}
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar sx={{ bgcolor: '#64b5f6', mr: 1 }}><MenuBookIcon /></Avatar>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>学生Aさん</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                22歳・大学生（女性）
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2">
                                「<strong>家賃を抑えつつ、友達と一緒に暮らしたい</strong>」という思いから、大学の友人とルームシェアをスタート。<br />
                                朝食を一緒に食べたり、テスト勉強をサポートし合ったりと、<strong>仲間意識</strong>が生まれたのが最大のメリットです。
                            </Typography>
                        </Paper>
                    </Grid>
                    {/* 社会人の体験談 */}
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar sx={{ bgcolor: '#81c784', mr: 1 }}><GroupIcon /></Avatar>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>社会人Bさん</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                29歳・IT企業勤務（男性）
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2">
                                「<strong>一人暮らしは寂しいし、生活コストも高い</strong>」と感じていたので、同年代の社会人3人でルームシェア。<br />
                                仕事帰りにリビングで情報交換したり、<strong>多様な価値観</strong>に触れられる点が魅力です。
                            </Typography>
                        </Paper>
                    </Grid>
                    {/* フリーランスの体験談 */}
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar sx={{ bgcolor: '#ffd54f', mr: 1 }}><EmojiPeopleIcon /></Avatar>
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>フリーランスCさん</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                34歳・Webデザイナー（女性）
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2">
                                「<strong>仕事の刺激や人との交流</strong>がほしくてルームシェアを選択。<br />
                                異業種の仲間から<Chip label="新しいアイデア" size="small" color="primary" />をもらえるのが大きなメリットでした。
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ my: 4 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 1, mt: 3 }}>
                        主なルームシェアのメリット
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="家賃・光熱費などの生活コストを大幅削減できる" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="孤独感の軽減や、日常のささいな悩みもシェアできる" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="異なる価値観やライフスタイルに触れ、視野が広がる" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="家事や役割分担で生活スキルがアップする" />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText primary="困った時に助け合える安心感がある" />
                        </ListItem>
                    </List>
                </Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Box sx={{ my: 2, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                            <img
                                src="/images/blog/share-cooking_roomshare-taikendan-samazamana-nendai-shokugyo-no-hitobito-ga-kataru-kyodo-seikatsu-no-real.jpg"
                                alt="ナチュラルなキッチンでルームメイトが一緒に料理を作り、笑顔でコミュニケーションを取る様子。共同作業による信頼関係の構築や家事分担の雰囲気が伝わる明るい場面"
                                style={{
                                    width: '100%',
                                    display: 'block'
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 直面した課題と解決策 */}
            <Box id="challenges" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    直面した課題とその解決策
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア生活には予想外のトラブルや、衝突がつきもの。実際にどんな課題があったのか、そしてどのように乗り越えたのか、リアルな声を紹介します。
                </Typography>
                <Grid container spacing={2}>
                    {/* 課題1: プライバシーの確保 */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>課題1：プライバシーの確保</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「共有スペースが多いと、一人になれる時間が少なくてストレスを感じることも…」（学生Aさん）
                                </Typography>
                                <Typography variant="body2">
                                    <strong>解決策：</strong>「自分の時間」を尊重するために、<strong>部屋の使い方や生活リズムを最初にしっかり話し合う</strong>のが大切です。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* 課題2: 家事の分担 */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>課題2：家事・掃除の分担</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「掃除やゴミ出しをめぐって、言いにくい不満が溜まることが…」（社会人Bさん）
                                </Typography>
                                <Typography variant="body2">
                                    <strong>解決策：</strong> <strong>当番制</strong>や<strong>チェックリスト</strong>を導入して、ルール化することで解消できました。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* 課題3: 金銭トラブル */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>課題3：お金の管理・トラブル</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「共用の消耗品や光熱費の清算タイミングで揉めたことも」（フリーランスCさん）
                                </Typography>
                                <Typography variant="body2">
                                    <strong>解決策：</strong> 共有アプリや電子マネー決済を活用し、<strong>お金の流れを見える化</strong>。定期的に精算日を決めておくと安心です。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    {/* 課題4: 生活習慣・価値観の違い */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>課題4：生活リズムや価値観の違い</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    「夜型と朝型、食事スタイルの違いでイライラすることも…」
                                </Typography>
                                <Typography variant="body2">
                                    <strong>解決策：</strong> お互いの違いを否定せず、<strong>譲れる点・譲れない点を明確に</strong>しておくのが大切です。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                    <img
                        src="/images/blog/share-communication_roomshare-taikendan-samazamana-nendai-shokugyo-no-hitobito-ga-kataru-kyodo-seikatsu-no-real.jpg"
                        alt="共有リビングのソファで入居者同士が真剣に話し合い、課題を解決しようと意見交換しているシーン。柔らかな照明と落ち着いた色調が安心感を与える空間"
                        style={{
                            width: '100%',
                            display: 'block'
                        }}
                    />
                </Box>
            </Box>

            {/* 人間関係の変化と成長 */}
            <Box id="relation-changes" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    人間関係の変化と成長
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    共同生活を続ける中で、<strong>人間関係にも大きな変化</strong>がありました。最初は遠慮し合っていた関係も、徐々に信頼や友情が芽生えていくものです。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon><GroupIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="多世代・多職種の交流が新たな価値観を生む"
                            secondary="学生と社会人、フリーランスなど異なる視点が刺激になり、お互いを尊重する空気が生まれます"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><EmojiPeopleIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="衝突を乗り越え、深まる信頼関係"
                            secondary="小さなトラブルを一緒に解決することで、絆が強まります"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon><TipsAndUpdatesIcon color="primary" /></ListItemIcon>
                        <ListItemText
                            primary="コミュニケーション能力や自己主張力の成長"
                            secondary="相手の立場を考えつつ自分の意見を伝える力が自然と身に付きます"
                        />
                    </ListItem>
                </List>
                <Box sx={{ my: 3, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                    <img
                        src="/images/blog/share-friends_roomshare-taikendan-samazamana-nendai-shokugyo-no-hitobito-ga-kataru-kyodo-seikatsu-no-real.jpg"
                        alt="ウッドデッキのバルコニーでルームメイトたちがリラックスしながらお茶を飲み、笑顔で会話する様子。夕暮れ時の暖色の光が友情と安心感を象徴している"
                        style={{
                            width: '100%',
                            display: 'block'
                        }}
                    />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    実際の体験談では「一生の友人ができた」「自分を客観的に見られるようになった」といった声も多く聞かれます。
                </Typography>
            </Box>

            {/* アドバイス */}
            <Box id="advice" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア経験者からのアドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Paper elevation={1} sx={{ p: 3, borderRadius: 2, bgcolor: '#f8fafc', mb: 2 }}>
                    <Typography variant="h5" component="h3" sx={{ mb: 2 }}>
                        失敗しないためのポイント
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="入居前にルール・価値観をしっかり共有"
                                secondary="生活リズムや家事分担、訪問者の可否など細かい点も話し合っておく"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="何かあった時は、我慢せずにすぐ相談"
                                secondary="問題が小さいうちに話し合うことで、大きなトラブルを防げます"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="お互いのプライベートを尊重"
                                secondary="干渉しすぎず、適度な距離感を大切に"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                            <ListItemText
                                primary="清算・家賃管理はアプリやシステムを活用"
                                secondary="お金の流れを明確にすることで、後々のトラブルが激減します"
                            />
                        </ListItem>
                    </List>
                </Paper>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        ルームシェアがうまくいくかどうかは、<strong>最初のルール決めとコミュニケーション</strong>にかかっています。住む人全員が安心できる環境をつくるため、「話し合い」を面倒がらず大切にしましょう。
                    </Typography>
                </Box>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 4 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                    ルームシェアは、<strong>経済的なメリット</strong>とともに、<strong>人間関係や自分自身の成長</strong>にもつながる魅力的な住まい方です。一方で、<strong>トラブルリスク</strong>もゼロではありません。
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                    しかし、最初にしっかりと話し合いを重ね、「違い」を認め合いながら暮らすことで、きっと<span style={{ color: '#1565c0', fontWeight: 'bold' }}>人生の宝物になる出会い</span>や経験が得られるはずです。
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Chip label="ルームシェア" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="共同生活" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="人間関係" color="primary" variant="outlined" />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-taikendan-samazamana-nendai-shokugyo-no-hitobito-ga-kataru-kyodo-seikatsu-no-real',
    slug: 'roomshare-taikendan-samazamana-nendai-shokugyo-no-hitobito-ga-kataru-kyodo-seikatsu-no-real',
    title: 'ルームシェア体験談：様々な年代・職業の人々が語る共同生活のリアル',
    excerpt: '学生・社会人・フリーランス体験談 きっかけとメリット 直面した課題 人間関係の変化 アドバイス',
    coverImage: '/images/blog/cover-living-share_roomshare-taikendan-samazamana-nendai-shokugyo-no-hitobito-ga-kataru-kyodo-seikatsu-no-real.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。白を基調としたインテリアと観葉植物が調和し、安心感と交流の雰囲気が伝わる。',
    publishedAt: '2024-06-13T00:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '住まい', 'ライフスタイル'],
    tags: ['ルームシェア', '共同生活', '体験談', '人間関係', 'メリット・デメリット'],
    author: {
        name: '高橋 友里',
        avatar: '/images/avatars/yuri-takahashi.jpg',
        bio: '不動産ライター・ルームシェア経験をもとに、暮らしや住まいのリアルな情報を発信。10年以上の取材と実体験から、誰でも安心して始められるルームシェアのコツを提案します。',
        profilePicture: '/images/avatars/yuri-takahashi.jpg'
    },
    viewCount: 1340,
    contentComponent: PostContent
};

export default post;